if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const clampQuantityToHtmlMinMax = () => {
          try {
            const quantityInput = this.form.querySelector('input[name="quantity"]');
            if (quantityInput) {
              const minAttr = quantityInput.min ? parseInt(quantityInput.min, 10) : null;
              const maxAttr = quantityInput.max ? parseInt(quantityInput.max, 10) : null;
              let currentQty = parseInt(quantityInput.value, 10);

              if (isNaN(currentQty) && minAttr !== null) {
                currentQty = minAttr;
              }

              if (minAttr !== null && currentQty < minAttr) {
                currentQty = minAttr;
              }
              if (maxAttr !== null && currentQty > maxAttr) {
                currentQty = maxAttr;
              }

              if (!isNaN(currentQty)) {
                quantityInput.value = currentQty;
              }
            }
          } catch (e) {
            console.error('Quantity clamp error (product form submit):', e);
          }
        };

        const performAddToCart = () => {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          clampQuantityToHtmlMinMax();

          const formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }

              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(response);
              }
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');
            });
        };

        let orderLimit = null;
        try {
          const variantId = this.variantIdInput && this.variantIdInput.value;
          if (variantId) {
            const productInfoEl = this.closest('[data-product-id]');
            const productId = productInfoEl ? productInfoEl.dataset.productId : null;
            if (productId) {
              if (!this.variantOrderLimits) {
                const limitsScript = document.getElementById(`VariantOrderLimits-${productId}`);
                if (limitsScript) {
                  try {
                    this.variantOrderLimits = JSON.parse(limitsScript.textContent || limitsScript.innerHTML || '{}');
                  } catch (e) {
                    console.error('Error parsing VariantOrderLimits JSON:', e);
                    this.variantOrderLimits = {};
                  }
                } else {
                  this.variantOrderLimits = {};
                }
              }
              const rawLimit = this.variantOrderLimits[variantId];
              if (rawLimit !== null && rawLimit !== undefined) {
                const parsedLimit = parseInt(rawLimit, 10);
                if (!isNaN(parsedLimit) && parsedLimit > 0) {
                  orderLimit = parsedLimit;
                }
              }
            }
          }
        } catch (e) {
          console.error('Error reading variant order limits:', e);
        }

        if (!orderLimit) {
          performAddToCart();
          return;
        }

        // Enforce cumulative variant-level order limit using current cart contents
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((res) => (res.ok ? res.json() : Promise.reject(res)))
          .then((cart) => {
            const variantId = this.variantIdInput && this.variantIdInput.value;
            if (!variantId) {
              performAddToCart();
              return;
            }

            const existingLine = cart.items.find((item) => String(item.variant_id) === String(variantId));
            const existingQty = existingLine ? existingLine.quantity : 0;

            const quantityInput = this.form.querySelector('input[name="quantity"]');
            let requested = quantityInput ? parseInt(quantityInput.value, 10) : 1;
            if (isNaN(requested) || requested < 1) {
              requested = 1;
            }

            const maxAdditional = orderLimit - existingQty;

            if (maxAdditional <= 0) {
              this.error = true;
              const message =
                window.variantStrings && window.variantStrings.orderLimitReached
                  ? window.variantStrings.orderLimitReached.replace('[limit]', orderLimit)
                  : `You can only purchase ${orderLimit} of this item.`;
              this.handleErrorMessage(message);
              this.submitButton.classList.remove('loading');
              this.querySelector('.loading__spinner').classList.add('hidden');
              this.submitButton.removeAttribute('aria-disabled');
              return;
            }

            if (requested > maxAdditional) {
              requested = maxAdditional;
            }

            if (quantityInput) {
              quantityInput.value = requested;
            }

            performAddToCart();
          })
          .catch((e) => {
            console.error('Error checking cart before add:', e);
            // Fall back to normal behavior
            performAddToCart();
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
